<template>
    <div>
        <div v-if="showSuggestions" @click="showSuggestions = false" class="product-search-input-backdrop"></div>
        <div class="product-search-input">
            <input @focus="showSuggestions = true" class="product-search-input-form" type="search" v-model="searchStr" placeholder="Search by barcode, SKU, or name" />
            <div class="suggestion-container" v-if="showSuggestions">
                <div class="p-2" v-if="searching">Searching...</div>
                <div class="p-2" v-else-if="!products.length">No Products found with stock.</div>
                <div @click="handleSelection(product)" class="suggestion-item" v-for="product of products" :key="product.id">
                    <div class="sugg-title">{{ product.name }}</div>
                    <div class="sugg-meta">SKU: {{ product.supplier_sku || '—' }}</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { http } from "../../utils/http"

export default {
    emits: ["change"],
    data() {
        return {
            searching: false,
            searchStr: "",
            products: [],
            searchTimeout: null,
            showSuggestions: false,
        }
    },
    props: {
        defaultProductId: { type: String, default: "" },
        // Optional filter: only show products that have stock in a specific warehouse
        warehouseIdFilter: { type: [String, Number], default: "" },
        // Optional filter: if provided (true for Front, false for Back), require stock in that room
        roomFrontFilter: { type: Boolean, default: null },
    },
    mounted() {
        this.fetchProducts()
        if (this.defaultProductId) {
            // Load the preset product name for visual confirmation in the input.
            // Do not auto-select here to avoid adding the wrong item implicitly.
            this.fetchProducts(this.defaultProductId)
            this.prefillSearchFromProductId(this.defaultProductId)
        }
    },
    methods: {
        // Allow parent components (e.g., scanner-enabled pages) to programmatically
        // set the search query and trigger a lookup. This supports workflows where
        // a barcode scan should immediately search products without manual typing.
        setSearchQuery(value) {
            this.searchStr = value || ""
            // Trigger a search immediately for snappy UX
            clearTimeout(this.searchTimeout)
            this.fetchProducts()
            this.showSuggestions = true
        },
        async prefillSearchFromProductId(id) {
            try {
                const res = await http.get(`/items/${id}`)
                const item = res.data?.result
                if (item && (item.name || item.item_name)) {
                    this.searchStr = item.name || item.item_name
                }
            } catch (err) {
                // Silent fail – this is only for UX polish
            }
        },
        async fetchProducts(defaultProductId = "") {
            this.searching = true
            try {
                const res = await http.get("/stock/inventory", {
                    params: {
                        product_id: defaultProductId || "",
                        variants: true,
                        search: this.searchStr,
                        smart_search: true,
                        limit: 10,
                        return_ledger_entries: true,
                    },
                })
                // Base: must have at least one ledger entry
                let results = (res.data.result || []).filter((item) => item.ledgerEntries && item.ledgerEntries.length)
                // Warehouse filter (optional)
                if (this.warehouseIdFilter) {
                    results = results.filter((item) => item.ledgerEntries.some((le) => String(le.warehouse) == String(this.warehouseIdFilter)))
                }
                // Room filter (optional)
                if (this.roomFrontFilter !== null) {
                    results = results.filter((item) => {
                        const le = this.warehouseIdFilter
                            ? item.ledgerEntries.find((le) => String(le.warehouse) == String(this.warehouseIdFilter))
                            : item.ledgerEntries[0]
                        if (!le) return false
                        return this.roomFrontFilter ? Number(le.stock_value) > 0 : Number(le.stock_value_back_room) > 0
                    })
                }
                this.products = results
                // Do not auto-select here; the parent page handles auto-adding
            } catch (err) {
                console.log("error in loading items", err)
            }
            this.searching = false
        },
        handleSelection(product) {
            this.showSuggestions = false
            this.$emit("change", product)
            this.searchStr = product.name
        },
        clear() {
            this.searchStr = ""
        },
    },
    watch: {
        searchStr(e) {
            clearTimeout(this.searchTimeout)
            this.searchTimeout = setTimeout(() => {
                this.fetchProducts()
            }, 1000)
        },
    },
}
</script>

<style lang="scss">
@import "../../assets/variables.scss";

/* Main Container */
.product-search-input { 
    position: relative; 
    width: 100%; 
}

/* Search Input Field */
.product-search-input-form {
    border: 1px solid $lightBorderColor;
    border-radius: 6px;
    padding: 10px 14px;
    width: 100%;
    outline: none;
    height: 40px;
    font-size: 14px;
    background: white;
    color: $textColor;
    transition: border-color 0.2s ease;

    &:focus {
        border-color: $primaryColor;
        box-shadow: 0 0 0 3px rgba($primaryColor, 0.1);
    }

    &::placeholder {
        color: rgba($textColor, 0.5);
        font-size: 13px;
    }
}

/* Suggestion Dropdown Container */
.suggestion-container {
    position: absolute;
    left: 0;
    top: 100%;
    margin-top: 8px;
    background: white;
    width: 100%;
    min-width: 460px;
    z-index: 1000;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    border-radius: 8px;
    border: 1px solid $lightBorderColor;
    overflow: hidden;
    max-height: 400px;
    overflow-y: auto;

    /* Suggestion Item Styling */
    .suggestion-item {
        padding: 12px 16px;
        cursor: pointer;
        border-bottom: 1px solid rgba($lightBorderColor, 0.5);
        font-size: 13px;
        line-height: 1.4;
        transition: background-color 0.15s ease;

        &:last-child {
            border-bottom: none;
        }

        &:hover {
            background: lighten($primaryColor, 45%);
            color: $primaryColor;
        }
    }

    /* Suggestion Title and Meta */
    .sugg-title { 
        font-weight: 600; 
        margin-bottom: 4px;
        color: $textColor;
    }

    .sugg-meta { 
        font-size: 12px; 
        opacity: 0.7;
        color: $textColor;
    }

    /* Loading and Empty States */
    .p-2 {
        padding: 16px;
        text-align: center;
        color: rgba($textColor, 0.6);
        font-size: 13px;
    }
}

/* Backdrop for closing dropdown */
.product-search-input-backdrop {
    position: fixed;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0);
    z-index: 999;
}

/* Dark Theme Overrides */
.dark .input-group-prepend .input-group-text {
    background: $darkInputBg;
    border-color: $darkBorderColor;
}

.dark .input-group-text i {
    color: $darkTextColor;
}

.dark .product-search-input-form {
    border-color: $darkBorderColor;
    background: $darkInputBg;
    color: $darkTextColor;

    &:focus {
        border-color: $secondaryColorDark;
        box-shadow: 0 0 0 3px rgba($secondaryColorDark, 0.15);
    }

    &::placeholder {
        color: rgba($darkTextColor, 0.5);
    }
}

.dark .suggestion-container {
    background: $darkCardBg;
    border-color: $darkBorderColor;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);

    .suggestion-item {
        border-bottom-color: rgba($darkBorderColor, 0.5);

        &:hover {
            background: rgba($secondaryColorDark, 0.2);
            color: $secondaryColorDark;
        }
    }

    .sugg-title,
    .sugg-meta {
        color: $darkTextColor;
    }

    .p-2 {
        color: rgba($darkTextColor, 0.6);
    }
}
</style>
